import { useCallback, useEffect, useRef, useState } from "react";

function useTableProgressiveRows({
  totalRows,
  renderBatchSize,
  tableLoading,
}: {
  totalRows: number;
  renderBatchSize?: number;
  tableLoading?: boolean;
}) {
  const batchSize =
    renderBatchSize && renderBatchSize > 0 ? renderBatchSize : Infinity;
  const [visibleRowCount, setVisibleRowCount] = useState(batchSize);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const hasMoreRows = visibleRowCount < totalRows;

  useEffect(() => {
    setVisibleRowCount(batchSize);
  }, [batchSize]);

  const handleLoadMore = useCallback(() => {
    setVisibleRowCount((count) => Math.min(count + batchSize, totalRows));
  }, [batchSize, totalRows]);

  useEffect(() => {
    if (
      !hasMoreRows ||
      tableLoading ||
      !loadMoreRef.current ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          handleLoadMore();
        }
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMoreRows, tableLoading, visibleRowCount, handleLoadMore]);

  return { visibleRowCount, hasMoreRows, loadMoreRef, handleLoadMore };
}

export default useTableProgressiveRows;
